import {ProductDataStore} from "../../stores/productDataStore";
import {ConfigurationObjectService} from "./configurationObjectService";
import {DrawingConfigurationInterface} from "./interface/drawingConfigurationInterface";
import {MullionInterface} from "./interface/mullionInterface";
import {AppStateService} from "../../services/appStateService";
import {ShapeService} from "../../services/shapeService";
import {intSashesAndMullionsConfigurationInterface} from "./interface/intSashesAndMullionsConfigurationInterface";
import cloneDeep from "lodash/cloneDeep";
import {ProfileInterface} from "./interface/profileInterface";

export class DrawingConfigurationService {
    private productDataStore: ProductDataStore;
    private configurationObjectService: ConfigurationObjectService;
    private configurationObject: DrawingConfigurationInterface;
    private shapeService: ShapeService;

    private sashConfiguration;
    private sashes;
    private readonly shape;
    private readonly standardFields;
    private readonly CASEMENT_WINDOW_FUNCTION_ID = 11;

    private INITIAL_SASH_ID: number = 1;
    private INITIAL_MULLION_ID: number = 1;
    private DEFAULT_HINGE_SIDE_ID: number = 1;
    private verticalMullions: number;
    private horizontalMullions: number;
    private appStateService: AppStateService;
    private canBeTurnedIntoCasementWindow: boolean;
    private mullions: any;
    private initialSashes: any;
    private hasFixedInSash: boolean = false;

    constructor() {
        this.appStateService = new AppStateService();
        this.productDataStore = new ProductDataStore();
        this.configurationObjectService = new ConfigurationObjectService();
        this.shapeService = new ShapeService();

        this.sashConfiguration = this.productDataStore.getSashConfiguration();
        this.standardFields = this.sashConfiguration.standardFields;
        this.sashes = this.sashConfiguration.sashes;
        this.initializeSashIndexes();
        this.initialSashes = cloneDeep(this.sashes);
        this.shape = this.sashConfiguration.shape;
        this.verticalMullions = this.productDataStore.getAttributeByName("dividersVertical");
        this.horizontalMullions = this.productDataStore.getAttributeByName("dividersHorizontal");
        this.canBeTurnedIntoCasementWindow = this.productDataStore.getAttributeByName('canBeTurnedIntoCasementWindow')
    }

    /**
     * Just to clarify:
     * divider - it's a mullion between main sashes
     * mullion - it's a regular mullion in sash which divide glass into internal sashes
     * muntin - it's a mullion which does not divide glass, it can be inside, outside or between glass
     * We have to remember that in the configuration DIVIDERS AND MULLIONS ARE JUST CALLED MULLIONS!.
     */
    getDrawingConfig()
    {
        this.hasFixedInSashAccessory();
        this.updateMullionsCount();
        this.setBaseStructure();
        this.setDimensions();
        this.setFrameColors();
        this.setMuntinsSize();
        this.setMuntinsColors();
        this.setShape();
        this.setCasementWindowConfiguration();
        this.updateHingeSide();

        const mainSashes = this.setMainSashConfiguration();

        this.generateIntSashesWithMullions(mainSashes);
        this.setMuntins(mainSashes);
        // if we want to refresh the drawing we need to update timestamp property
        this.configurationObject.timestamp = Date.now();
        this.appStateService.setCurrentDrawingConfiguration(this.configurationObject);

        return this.configurationObject;
    }

    /**
     * We need this function to dynamically change sash function because of the casement window rule
     * If we set specific width/height ratio then we need to change the window
     */
    private setCasementWindowConfiguration()
    {
        if (this.canBeTurnedIntoCasementWindow) {
            if (this.productDataStore.getAttributeByName('windowFunctionId') === this.CASEMENT_WINDOW_FUNCTION_ID) {
                this.sashes[0].handlePosition = 'T';
                this.sashes[0].sashType = 'K';
            } else {
                this.sashes[0] = this.productDataStore.getInitialSashConfiguration().sashes[0];
            }
        }
    }

    private setMuntinsSize()
    {
        const muntinSizeData = this.productDataStore.getSelectedOptionData('mullionSize');

        if (muntinSizeData === null) {
            return;
        }

        this.configurationObject.muntins.size = muntinSizeData.attributes.size;
    }

    private setMuntins(sashes)
    {

        const muntins = this.productDataStore.getAttributeByName('muntins');

        if (
            !muntins.isCustomLayout &&
            (muntins.horizontalCount + muntins.verticalCount) === 0
        ) {
            return;
        }

        const usedProfiles = this.configurationObjectService.getProfiles();
        const dividerProfile: ProfileInterface = usedProfiles.find(o => o.type === 'divider');
        const frameProfile: ProfileInterface = usedProfiles.find(o => o.type === 'frame');
        const sashProfile: ProfileInterface = usedProfiles.find(o => o.type === 'sash');

        const dividerOffset = dividerProfile.spaceGlass || 25;
        const frameOffset = frameProfile.spaceGlass || 50;
        const sashOffset = frameProfile.spaceSash + sashProfile.spaceGlass || 100;

        let profileOffset = frameOffset;
        let muntinId = 1;

        sashes.forEach(sash => {

            if (sash.intSashes.length === 0) {
                if (muntins.isCustomLayout) {
                    const customLayout = muntins.layout[sash.id] ?? [];
                    customLayout.forEach(muntin => {
                        const newMuntin = this.getNewCustomMuntin(sash, profileOffset, dividerOffset, muntinId, muntin);
                        sash.muntins.push(newMuntin);
                    });
                } else {
                    for (let i = 1; i <= muntins.horizontalCount; i++) {
                        const newMuntin = this.getNewMuntin(sash, profileOffset, dividerOffset, muntinId, i, 'horizontal', null, muntins.verticalCount, muntins.horizontalCount);
                        sash.muntins.push(newMuntin);
                    }

                    for (let i = 1; i <= muntins.verticalCount; i++) {
                        const newMuntin = this.getNewMuntin(sash, profileOffset, dividerOffset, muntinId, i, 'vertical', null, muntins.verticalCount, muntins.horizontalCount);
                        sash.muntins.push(newMuntin);
                    }

                }
            } else {
                sash.intSashes.forEach(sash => {
                    profileOffset = sashOffset;
                    if (sash.filling.type !== 'pvc_panels') {
                        if (muntins.isCustomLayout) {
                            const customLayout = muntins.layout[sash.id] ?? [];
                            customLayout.forEach(muntin => {
                                const newMuntin = this.getNewCustomMuntin(sash, profileOffset, dividerOffset, muntinId, muntin);
                                sash.muntins.push(newMuntin);
                            });
                        } else {
                            for (let i = 1; i <= muntins.horizontalCount; i++) {
                                const newMuntin = this.getNewMuntin(sash, profileOffset,dividerOffset,muntinId, i, 'horizontal', null, muntins.verticalCount, muntins.horizontalCount);
                                sash.muntins.push(newMuntin);
                            }
                            for (let i = 1; i <= muntins.verticalCount; i++) {
                                const newMuntin = this.getNewMuntin(sash, profileOffset, dividerOffset, muntinId, i, 'vertical', null, muntins.verticalCount, muntins.horizontalCount);
                                sash.muntins.push(newMuntin);
                            }
                        }
                    }
                });
            }

            muntinId++;
        });
    }

    private getNewMuntin(sash, profileOffset, dividerOffset, muntinId, muntinIndex, muntinDirection, muntinPosition, verticalMuntinsCount, horizontalMuntinsCount)
    {
        const glazingOffset = {
            top: sash.nearMullions.top !== -1 ? dividerOffset : profileOffset,
            left: sash.nearMullions.left !== -1 ? dividerOffset : profileOffset,
            right: sash.nearMullions.right !== -1 ? dividerOffset : profileOffset,
            bottom: sash.nearMullions.bottom !== -1 ? dividerOffset : profileOffset,
        };

        const glazingRect = {
            x: glazingOffset.left,
            y: glazingOffset.top,
            width: sash.width - glazingOffset.left - glazingOffset.right,
            height: sash.height - glazingOffset.top - glazingOffset.bottom,
        };

        const muntinOffsetX = glazingRect.width / (verticalMuntinsCount + 1);
        const muntinOffsetY = glazingRect.height / (horizontalMuntinsCount + 1);

        let startX;
        let startY;
        let endX;
        let endY;

        if (muntinDirection === 'horizontal') {
            startX = glazingRect.x;
            endX = glazingRect.width + glazingRect.x;

            if (muntinPosition) {
                startY = (muntinPosition * glazingRect.height) + glazingOffset.top;
                endY = (muntinPosition * glazingRect.height) + glazingOffset.top;
            } else {
                startY = (muntinIndex * muntinOffsetY) + glazingOffset.top;
                endY = (muntinIndex * muntinOffsetY) + glazingOffset.top;
            }
        }

        if (muntinDirection === 'vertical') {
            if (muntinPosition) {
                startX = (muntinPosition * glazingRect.width) + glazingOffset.left;
                endX = (muntinPosition * glazingRect.width) + glazingOffset.left;
            } else {
                startX = (muntinIndex * muntinOffsetX) + glazingOffset.left;
                endX = (muntinIndex * muntinOffsetX) + glazingOffset.left;
            }

            startY = glazingRect.y;
            endY = glazingRect.height + glazingRect.y;
        }

        const newMuntin = Object.assign(
            this.configurationObjectService.getMuntin(),
            {
                id: muntinId,
                start: {
                    x: startX,
                    y: startY,
                },
                end: {
                    x: endX,
                    y: endY,
                }
            }
        );

        return newMuntin;
    }

    private getNewCustomMuntin(sash, profileOffset, dividerOffset, muntinId, muntinLayout)
    {

        // I'm subtracting "15" because glazing rect for some reason is bigger than we can see,
        // and some part is hidden under glazing bead, so if I want diagonal bars to looks like they are pointing to
        // the bottom corners I need to use this small hack
        const GLAZING_BEAD_SIZE = 15;

        const glazingOffset = {
            top: sash.nearMullions.top !== -1 ? dividerOffset : profileOffset,
            left: sash.nearMullions.left !== -1 ? dividerOffset : profileOffset,
            right: sash.nearMullions.right !== -1 ? dividerOffset : profileOffset,
            bottom: sash.nearMullions.bottom !== -1 ? dividerOffset : profileOffset,
        };

        const glazingRect = {
            x: glazingOffset.left,
            y: glazingOffset.top,
            width: sash.width - glazingOffset.left - glazingOffset.right,
            height: sash.height - glazingOffset.top - glazingOffset.bottom - GLAZING_BEAD_SIZE,
        };

        let startX;
        let startY;
        let endX;
        let endY;

        // vertical muntin
        if (muntinLayout.startX === muntinLayout.endX) {
            startX = (muntinLayout.startX * glazingRect.width) + glazingRect.x;
            endX = (muntinLayout.startX * glazingRect.width) + glazingRect.x;
            startY = (muntinLayout.startY * glazingRect.height) + glazingOffset.top;
            endY = (muntinLayout.endY * glazingRect.height) + glazingOffset.top;
        }

        // horizontal muntin or diagonal bars
        if (
            muntinLayout.startY === muntinLayout.endY ||
            (
                Math.abs(muntinLayout.startX - muntinLayout.endX) === 1 &&
                Math.abs(muntinLayout.startY - muntinLayout.endY) === 1
            )
        ) {
            startX = (muntinLayout.startX * glazingRect.width) + glazingOffset.left;
            endX = (muntinLayout.endX * glazingRect.width) + glazingOffset.left;
            startY = (muntinLayout.startY * glazingRect.height) + glazingRect.y;
            endY = (muntinLayout.endY * glazingRect.height) + glazingRect.y;
        }

        const newMuntin = Object.assign(
            this.configurationObjectService.getMuntin(),
            {
                id: muntinId,
                start: {
                    x: startX,
                    y: startY,
                },
                end: {
                    x: endX,
                    y: endY,
                }
            }
        );

        return newMuntin;
    }

    private setMuntinsColors()
    {
        const selectedMuntinOutsideColor = this.productDataStore.getMuntinOutsideColor();
        const selectedMuntinInsideColor = this.productDataStore.getMuntinInsideColor();

        if (selectedMuntinInsideColor !== null && selectedMuntinOutsideColor !== null) {
            this.configurationObject.muntins.colorInner.color = this.validateColorHexCode(selectedMuntinInsideColor.attributes.hex);
            this.configurationObject.muntins.colorOuter.color = this.validateColorHexCode(selectedMuntinOutsideColor.attributes.hex);
        }
    }

    private updateHingeSide()
    {
        let hingeType = null;
        const hingeTypes = ['windowSingleTurnTiltOrTurnHingeSide', 'activeSash', 'activeDoorSash', 'mirrorReflection', 'hingeSide'];
        for (const type of hingeTypes) {
            if (this.productDataStore.getAvailableOptionsByName(type).length !== 0) {
                hingeType = type;
            }
        }

        if (hingeType === null) {
            return;
        }

        const selectedHingeSideId = this.productDataStore.getSelectedOptionByName(hingeType).id;
        switch (hingeType) {
            case 'activeSash':
                this.updateActiveSash(selectedHingeSideId);
                break;
            case 'hingeSide':
                this.updateHingeSideOption(selectedHingeSideId);
                break;
            case 'activeDoorSash':
            case 'mirrorReflection':
                this.mirrorSashes(selectedHingeSideId);
                break;
        }
    }

    private updateMullionsCount()
    {
        this.verticalMullions = this.productDataStore.getAttributeByName("mullions").verticalCount;
        this.horizontalMullions = this.productDataStore.getAttributeByName("mullions").horizontalCount;
        this.mullions = this.productDataStore.getAttributeByName("mullions");
    }

    private setBaseStructure()
    {
        this.configurationObject = this.configurationObjectService.getBaseConfiguration();
    }

    private setDimensions()
    {
        this.configurationObject.width = this.productDataStore.getAttributeByName('width');
        this.configurationObject.height = this.productDataStore.getAttributeByName('height');
    }

    private setFrameColors()
    {
        const frameInnerColor = this.productDataStore.getInsideColor().attributes;
        const frameOuterColor = this.productDataStore.getOutsideColor().attributes;

        this.configurationObject.colors.frame.inner.color = this.validateColorHexCode(frameInnerColor.hex);
        this.configurationObject.colors.frame.outer.color = this.validateColorHexCode(frameOuterColor.hex);
        this.configurationObject.colors.sash.inner.color = this.validateColorHexCode(frameInnerColor.hex);
        this.configurationObject.colors.sash.outer.color = this.validateColorHexCode(frameOuterColor.hex);

        // id value doesn't matter
        this.configurationObject.colors.frame.inner.id = 1;
        this.configurationObject.colors.sash.inner.id = 1;
        this.configurationObject.colors.frame.outer.id = 1;
        this.configurationObject.colors.sash.outer.id = 1;
    }

    private validateColorHexCode(hexCode: string)
    {
        return hexCode.replace('#', '');
    }

    private setShape()
    {
        this.configurationObject.shape = this.shapeService.getShape();
        // stub associated with iccdraw.js library usage
        if (
            this.configurationObject.shape.shape === 'arc' &&
            ['F', 'L', 'R'].indexOf(this.configurationObject.shape.type) === -1
        ) {
            this.configurationObject.shape.type = 'F';
        }
    }

    private setMainSashConfiguration(): Array<any>
    {
        const mainSashes = [];
        const sashWidth = this.getMainSashWidth();

        let mullionId = this.INITIAL_MULLION_ID;
        let sashId = this.INITIAL_SASH_ID;
        this.sashes.forEach(sash => {
        const sashType = sash.sashType === 'F' && this.hasFixedInSash ? 'FF' : sash.sashType ;
            const sashXOffset = this.getMainSashOffsetX(sashId, sashWidth);
            const newSash = Object.assign(
                this.configurationObjectService.getSash(),
                {
                    id: sashId,
                    type: {
                        id: sash.id,
                        handlePosition: sash.handlePosition,
                        type: sashType
                    },
                    index: sash.index,
                    width: sashWidth,
                    height: this.getProductHeight(),
                    x: sashXOffset,
                    y: 0,
                }
            );

            if (this.isNotOneSashProduct()) {
                if (this.isNotLastSash(sashId)) {
                    const mullion: MullionInterface = Object.assign(
                        this.configurationObjectService.getMullion(),
                        {
                            id: mullionId,
                            direction: 'vertical',
                            position: this.getDividerOffsetX(sashId, sashWidth),
                            length: this.getProductHeight(),
                            adjacentSashes: {
                                left: [newSash.id],
                                right: [newSash.id + 1],
                            },
                            profileId: 5
                        }
                    );

                    newSash.nearMullions.right = mullion.id;
                    this.configurationObject.mullions.push(mullion);

                    mullionId++;
                }

                /**
                 * Probably You are surprised that we find index by subtract "-2" or "-3". This is
                 * because we set mullionId from "1" instead of "0" and we increment this number before action below
                 * we need to set mullions index like "mullionId - 2" or "mullionId - 3"
                 * to get the first or the last one mullion.
                 * Also we need to remember that we set nearMullions.left only for second and next ones sashes.
                 */
                if (this.isTheLastSash(sashId)) {
                    newSash.nearMullions.left = this.configurationObject.mullions[mullionId - 2].id;
                } else if (this.isNotFirstOrLastSash(sashId)) {
                    newSash.nearMullions.left = this.configurationObject.mullions[mullionId - 3].id;
                }
            }

            this.configurationObject.sashes.push(newSash);
            sashId++;

            mainSashes.push(newSash);
        });

        return mainSashes;
    }

    /**
     * The goal is to generate matrix structure of the int sashes, vertical and horizontal mullions IDs
     * so later it will be easier to create configuration of these and setup proper configurations.
     * To do that we have to set proper IDs of perpendicular mullions and adjacent sashes
     * which will be much easier having 3 matrix of IDs. This is because there are just simple
     * rules which we can use in the loops over the data (numbers of int sashes or mullions)
     */
    private getIntSashAndMullionMatrix(): intSashesAndMullionsConfigurationInterface
    {
        const intSashRows = this.horizontalMullions + 1;
        const intSashColumns = this.verticalMullions + 1;
        const lastRowSashesIds = [];
        const firstRowSashesIds = [];
        const firstColumnSashesIds = [];
        const lastColumnSashesIds = [];

        let sashMatrix = [];
        let horizontalMullionsMatrix = [];
        let verticalMullionsMatrix = [];
        let sashId = 1;

        for (let rowIndex = 0; rowIndex < intSashRows; rowIndex++) {
            sashMatrix[rowIndex] = [];
            for (let columnIndex = 0; columnIndex < intSashColumns; columnIndex++) {
                sashMatrix[rowIndex].push(sashId);

                if (rowIndex === 0) {
                    firstRowSashesIds.push(sashId);
                }

                if (rowIndex === intSashRows - 1) {
                    lastRowSashesIds.push(sashId);
                }

                if (columnIndex === 0) {
                    firstColumnSashesIds.push(sashId);
                }

                if (columnIndex === intSashColumns - 1) {
                    lastColumnSashesIds.push(sashId);
                }

                sashId++;
            }
        }

        const verticalMullions = this.mullions['vertical'];
        verticalMullions.forEach(mullion => {
            verticalMullionsMatrix.push(mullion.id);
        });

        const horizontalMullions = this.mullions['horizontal'];
        horizontalMullions.forEach((horizontalMullionsInTheRow, rowIndex) => {
            horizontalMullionsMatrix[rowIndex] = [];
            horizontalMullionsInTheRow.forEach(horizontalMullion => {
                horizontalMullionsMatrix[rowIndex].push(horizontalMullion.id);
            })
        });

        return {
            sashesIds: sashMatrix,
            verticalMullionsIds: verticalMullionsMatrix,
            horizontalMullionsIds: horizontalMullionsMatrix,
            maxSashId: sashId,
            maxMullionId: 100,
            lastRowSashesIds: lastRowSashesIds,
            firstRowSashesIds: firstRowSashesIds,
            firstColumnSashesIds: firstColumnSashesIds,
            lastColumnSashesIds: lastColumnSashesIds,
        };
    }

    private getProductHeight()
    {
        return this.configurationObject.height;
    }

    private getMainSashHeight()
    {
        return this.configurationObject.height;
    }

    private getAdjacentSashes(mullionDirection: string, mullionColumnIndex: number, sashesIds, horizontalMullionsRowIndex: number = null)
    {
        let adjacentSashes = {left:[], right: [], top: [], bottom: []};

        if (mullionDirection === 'vertical') {
            sashesIds.forEach(row => {
                adjacentSashes.left.push(row[mullionColumnIndex]);
                adjacentSashes.right.push(row[mullionColumnIndex + 1]);
            })
        }

        if (mullionDirection === 'horizontal') {
            adjacentSashes.top.push(sashesIds[horizontalMullionsRowIndex][mullionColumnIndex]);
            adjacentSashes.bottom.push(sashesIds[horizontalMullionsRowIndex + 1][mullionColumnIndex]);
        }

        return adjacentSashes;
    }

    private generateIntSashesWithMullions(mainSashes: Array<any>)
    {
        let intSashesAndMullionsConfiguration: intSashesAndMullionsConfigurationInterface = this.getIntSashAndMullionMatrix();
        let bottomPanelHorizontalMullionId = 99;
        let bottomPanelIntSashId = this.INITIAL_SASH_ID + 1;

        mainSashes.forEach(sash => {
            let currentInternalSashId = 1;
            if (this.productHasAnyMullions()) {
                const sashWidth = this.getMainSashWidth();
                const sashHeight = this.getHeightOfAllIntSashesWithGlazing();
                const verticalMullionHeight = this.getHeightOfAllIntSashesWithGlazing();

                const intSashRows = this.horizontalMullions + 1;
                const intSashColumns = this.verticalMullions + 1;
                const intSashWidth = sashWidth / (this.verticalMullions + 1);
                const intSashHeight = sashHeight / (this.horizontalMullions + 1);


                if (this.verticalMullions > 0) {
                    intSashesAndMullionsConfiguration.verticalMullionsIds.forEach((verticalMullionId, verticalMullionIndex) => {
                        const position = this.getMullionPosition('vertical', verticalMullionId, sashWidth);
                        const newMullion = Object.assign(
                            this.configurationObjectService.getMullion(),
                            {
                                id: verticalMullionId,
                                direction: 'vertical',
                                position: position,
                                length: verticalMullionHeight,
                                profileId: 5,
                                adjacentSashes: this.getAdjacentSashes('vertical', verticalMullionIndex, intSashesAndMullionsConfiguration.sashesIds),
                                perpendicularMullions: this.getPerpendicularMullionsForVerticalOne(verticalMullionIndex, intSashesAndMullionsConfiguration),
                            }
                        );
                        sash.intMullions.push(newMullion);
                    });
                }

                if (this.horizontalMullions > 0) {
                    intSashesAndMullionsConfiguration.horizontalMullionsIds.forEach((row, rowIndex) => {
                        row.forEach((mullionId, mullionIndexInRow) => {
                            const position = this.getMullionPosition('horizontal', mullionId, verticalMullionHeight, rowIndex);
                            const newMullion = Object.assign(
                                this.configurationObjectService.getMullion(),
                                {
                                    id: mullionId,
                                    direction: 'horizontal',
                                    position: position,
                                    length: intSashWidth,
                                    profileId: 5,
                                    adjacentSashes: this.getAdjacentSashes('horizontal', mullionIndexInRow, intSashesAndMullionsConfiguration.sashesIds, rowIndex),
                                }
                            );
                            sash.intMullions.push(newMullion);
                        });
                    });
                }

                // create int sashes
                for (let intSashRowIndex = 0; intSashRowIndex < intSashRows; intSashRowIndex++) {
                    for (let intSashColumnIndex = 0; intSashColumnIndex < intSashColumns; intSashColumnIndex++) {

                        const topNearMullionId = this.getTopNearMullionId(intSashesAndMullionsConfiguration.horizontalMullionsIds, intSashColumnIndex, intSashRowIndex);
                        const bottomNearMullionId = this.getBottomNearMullionId(intSashesAndMullionsConfiguration.horizontalMullionsIds, intSashColumnIndex, intSashRowIndex, intSashRows);
                        const leftNearMullionId = this.getLeftNearMullionId(intSashesAndMullionsConfiguration.verticalMullionsIds, intSashColumnIndex);
                        const rightNearMullionId = this.getRightNearMullionId(intSashesAndMullionsConfiguration.verticalMullionsIds, intSashColumnIndex, intSashColumns);

                        const newIntSash = Object.assign(
                            this.configurationObjectService.getIntSash(),
                            {
                                id: currentInternalSashId,
                                parentId: sash.id,
                                width: this.getInternalSashWidth(currentInternalSashId, sashWidth, intSashesAndMullionsConfiguration, sash.intMullions, leftNearMullionId, rightNearMullionId),
                                height: this.getInternalSashHeight(currentInternalSashId, sashHeight, intSashesAndMullionsConfiguration, sash.intMullions, topNearMullionId, bottomNearMullionId),
                                profileId: 5,
                                index: null,
                                x: this.getInternalSashOffsetX(currentInternalSashId, sashWidth, intSashesAndMullionsConfiguration, sash.intMullions, leftNearMullionId),
                                y: this.getInternalSashOffsetY(currentInternalSashId, sashHeight, intSashesAndMullionsConfiguration, sash.intMullions, topNearMullionId),
                                nearMullions: {
                                    top: topNearMullionId,
                                    bottom: bottomNearMullionId,
                                    left: leftNearMullionId,
                                    right: rightNearMullionId,
                                }
                            });

                        if (this.hasBottomPanel()) {
                            bottomPanelHorizontalMullionId = intSashesAndMullionsConfiguration.maxMullionId + 1;
                            if (this.isTheLastRowOfIntSashes(intSashRowIndex, intSashRows)) {
                                newIntSash.nearMullions.bottom = bottomPanelHorizontalMullionId;
                            }
                        }

                        sash.intSashes.push(newIntSash);
                        currentInternalSashId++;
                    }
                }
            } else if (sash.type.type !== 'F'){
                sash.intSashes.push(Object.assign(
                    this.configurationObjectService.getIntSash(),
                    {
                        id: currentInternalSashId,
                        parentId: sash.id,
                        width: sash.width,
                        height: this.getHeightOfAllIntSashesWithGlazing(),
                        profileId: 5,
                        x: 0,
                        y: 0,
                        nearMullions: {
                            top: -1,
                            bottom: this.hasBottomPanel() ? bottomPanelHorizontalMullionId : -1,
                            left: -1,
                            right: -1,
                        }

                    })
                );
                currentInternalSashId++;
            }

            // bottom panel
            if (this.hasBottomPanel()) {
                const newMullion = Object.assign(
                    this.configurationObjectService.getMullion(),
                    {
                        id: bottomPanelHorizontalMullionId,
                        direction: 'horizontal',
                        position: this.getBottomPanelInternalMullionOffsetY(),
                        length: sash.width,
                        profileId: 5,
                        adjacentSashes: {
                            top: intSashesAndMullionsConfiguration.lastRowSashesIds,
                            bottom: [bottomPanelIntSashId]
                        },
                        perpendicularMullions: {bottom: [], top: intSashesAndMullionsConfiguration.verticalMullionsIds}
                    }
                );
                sash.intMullions.push(newMullion);

                sash.intSashes.push(Object.assign(
                    this.configurationObjectService.getIntSash(),
                    {
                        id: bottomPanelIntSashId,
                        parentId: sash.id,
                        width: sash.width,
                        height: this.getBottomPanelHeight(),
                        profileId: 5,
                        x: 0,
                        y: this.getBottomPanelInternalSashOffsetY(),
                        filling: {
                            id: -1,
                            type: "pvc_panels"
                        },
                        nearMullions: {
                            top: bottomPanelHorizontalMullionId,
                            bottom: -1,
                            left: -1,
                            right: -1,
                        }

                    })
                );
            }
        });
    }

    private getPerpendicularMullionsForVerticalOne(verticalMullionIndex, intSashesAndMullionsConfiguration)
    {
        const leftMullionsIds = [];
        const rightMullionsIds = [];
        intSashesAndMullionsConfiguration.horizontalMullionsIds.forEach(row => {
            row.forEach((mullionId, index) => {
                if (index === verticalMullionIndex) {
                    leftMullionsIds.push(mullionId);
                }

                if (index === (verticalMullionIndex + 1)) {
                    rightMullionsIds.push(mullionId);
                }
            });
        });

        return {
            left: leftMullionsIds,
            right: rightMullionsIds,
        };
    }

    private getMullionPosition(direction, mullionId, sashDimension, rowIndex = null)
    {
        const mullions = direction === 'horizontal' ? this.mullions[direction][rowIndex] : this.mullions[direction];
        const mullion = mullions.filter(mullion => mullion.id === mullionId)[0];

        if (mullion.customPosition) {
            return mullion.customPosition;
        }
        return mullion.position * sashDimension;
    }

    private getBottomPanelInternalSashOffsetY()
    {
        return this.getHeightOfAllIntSashesWithGlazing();
    }

    private getBottomPanelInternalMullionOffsetY()
    {
        return this.getHeightOfAllIntSashesWithGlazing();
    }
    /**
     * Remember that we enumerate rows from "0" but number of rows starts with 1
     */
    private isTheLastRowOfIntSashes(sashRowIndex: number, numberOfRows: number)
    {
        return sashRowIndex === numberOfRows - 1;
    }

    private productHasAnyMullions()
    {
        return (this.verticalMullions + this.horizontalMullions) > 0;
    }

    private getTopNearMullionId(horizontalMullionsIds, intSashColumnIndex, intSashRowIndex)
    {
        return intSashRowIndex === 0 ? -1 : horizontalMullionsIds[intSashRowIndex - 1][intSashColumnIndex];
    }

    private getBottomNearMullionId(horizontalMullionsIds, intSashColumnIndex, intSashRowIndex, intSashRows)
    {
        return intSashRowIndex < intSashRows - 1 ? horizontalMullionsIds[intSashRowIndex][intSashColumnIndex] : -1;
    }

    private getLeftNearMullionId(verticalMullionsIds, intSashColumnIndex)
    {
        return intSashColumnIndex === 0 ? -1 : verticalMullionsIds[intSashColumnIndex - 1];
    }

    private getRightNearMullionId(verticalMullionsIds, intSashColumnIndex, intSashColumns)
    {
        return intSashColumnIndex < intSashColumns - 1 ? verticalMullionsIds[intSashColumnIndex] : -1;
    }

    getInternalSashOffsetX(internalSashId, mainSashWidth, intSashesAndMullionsConfiguration, mainSashIntMullions, leftNearMullionId)
    {
        if (intSashesAndMullionsConfiguration.firstColumnSashesIds.findIndex(id => id === internalSashId) > -1) {
            return 0;
        }

        const mullion = mainSashIntMullions.filter(mullion => mullion.id === leftNearMullionId)[0];
        return mullion.position;
    }

    getInternalSashOffsetY(internalSashId, intSashHeight, intSashesAndMullionsConfiguration, mainSashIntMullions, topNearMullionId)
    {
        if (intSashesAndMullionsConfiguration.firstRowSashesIds.findIndex(id => id === internalSashId) > -1) {
            return 0;
        }

        const mullion = mainSashIntMullions.filter(mullion => mullion.id === topNearMullionId)[0];
        return mullion.position;
    }

    getInternalSashWidth(internalSashId, mainSashWidth, intSashesAndMullionsConfiguration, mainSashIntMullions, leftNearMullionId, rightNearMullionId)
    {
        if (leftNearMullionId === -1 && rightNearMullionId === -1) {
            return mainSashWidth;
        }

        const rightNearMullion = mainSashIntMullions.filter(mullion => mullion.id === rightNearMullionId)[0];
        const leftNearMullion = mainSashIntMullions.filter(mullion => mullion.id === leftNearMullionId)[0];

        if (intSashesAndMullionsConfiguration.firstColumnSashesIds.findIndex(id => id === internalSashId) > -1) {
            return rightNearMullion.position;
        }

        if (intSashesAndMullionsConfiguration.lastColumnSashesIds.findIndex(id => id === internalSashId) > -1) {
            return mainSashWidth - leftNearMullion.position;
        }

        return rightNearMullion.position - leftNearMullion.position;
    }

    getInternalSashHeight(internalSashId, glazingHeight, intSashesAndMullionsConfiguration, mainSashIntMullions, topNearMullionId, bottomNearMullionId)
    {
        if (topNearMullionId === -1 && bottomNearMullionId === -1) {
            return glazingHeight;
        }

        const topNearMullion = mainSashIntMullions.filter(mullion => mullion.id === topNearMullionId)[0];
        const bottomNearMullion = mainSashIntMullions.filter(mullion => mullion.id === bottomNearMullionId)[0];

        if (intSashesAndMullionsConfiguration.firstRowSashesIds.findIndex(id => id === internalSashId) > -1) {
            return bottomNearMullion.position;
        }

        if (intSashesAndMullionsConfiguration.lastRowSashesIds.findIndex(id => id === internalSashId) > -1) {
            return glazingHeight - topNearMullion.position;
        }

        return bottomNearMullion.position - topNearMullion.position;
    }

    /**
     * Each product is divided into number of same width sashes
     * and we need to calculate offset for each main sash
     * where the first one has offset "0" and next ones have offset
     * of all previous sashes (starting from left)
     */
    private getMainSashOffsetX(sashIndex: number, sashWidth: number)
    {
        let offset = 0;
        for (let i = 1; i < sashIndex; i++) {
            offset += sashWidth;
        }
        return offset;
    }

    private getDividerOffsetX(sashIndex: number, sashWidth: number)
    {
        let offset = sashWidth;
        for (let i = 1; i < sashIndex; i++) {
            offset += sashWidth;
        }
        return offset;
    }

    private getMainSashWidth()
    {
        return this.configurationObject.width / this.standardFields;
    }

    private isNotLastSash(sashId: number) {
        return sashId !== this.standardFields;
    }

    private isTheLastSash(sashId: number) {
        return sashId === this.standardFields;
    }

    private isNotFirstOrLastSash(sashId: number) {
        return sashId > 1 && sashId < this.standardFields;
    }

    private isNotOneSashProduct() {
        return this.standardFields !== 1;
    }

    public getSashesArea() {
        let sashArea = 0;

        this.sashes.forEach((sash) => {
            sashArea += (sash.height / 1000) * (sash.width / 1000);
        });

        return sashArea;
    }

    private hasBottomPanel()
    {
        const productHeight = this.productDataStore.getAttributeByName('height');
        const productGlazingHeight = this.productDataStore.getAttributeByName('bottomPanel');
        if (!productGlazingHeight) {
            return false;
        }

        return productHeight !== productGlazingHeight;
    }

    /**
     * If You are wondering why bottom panel is calculated like this, well...
     * Someone had an idea to name glazing height selector on window doors as a "bottomPanel"
     * so now we have to "flip" calculations because bottomPanel means glazing height and
     * bottom panel is the rest of the product (product height - glazing height)
     */
    private getBottomPanelHeight()
    {
        const bottomPanelHeight = this.productDataStore.getAttributeByName('bottomPanel');
        if (!bottomPanelHeight) {
            return 0;
        }

        return this.getProductHeight() - bottomPanelHeight;
    }

    private getHeightOfAllIntSashesWithGlazing()
    {
        return this.getMainSashHeight() - this.getBottomPanelHeight();
    }

    private updateActiveSash(value:number) {
        switch (value) {
            case 1:
                this.sashes[0].handlePosition = 'R';
                this.sashes[0].sashType = 'DS';
                this.sashes[1].handlePosition = 'L';
                this.sashes[1].sashType = 'DK';
                break;
            case 2:
                this.sashes[0].handlePosition = 'R';
                this.sashes[0].sashType = 'DK';
                this.sashes[1].handlePosition = 'L';
                this.sashes[1].sashType = 'DS';
                break;
            case 3:
                this.sashes[0].handlePosition = 'R';
                this.sashes[0].sashType = 'DK';
                this.sashes[1].handlePosition = 'L';
                this.sashes[1].sashType = 'DK';
                break;
            default:
                throw new Error('Value doesn\'t match');
        }
    }

    private updateHingeSideOption(selectedHingeSideId:number) {
        const isDefaultInsideView = this.productDataStore.getAttributeByName('defaultInsideView');
        const selectedHandlePosition = this.productDataStore.getSelectedOptionData('handlePosition');
        let handlePosition = '';

        if (selectedHingeSideId === 1) {
            handlePosition = isDefaultInsideView ? 'L' : 'R';
        } else if (selectedHingeSideId === 2) {
            handlePosition = isDefaultInsideView ? 'R' : 'L';
        } else if (selectedHingeSideId === 3) {
            handlePosition = selectedHandlePosition.attributes.symbol;
        }

        if (this.sashes.length === 1) {
            this.sashes[0].handlePosition = handlePosition;
            return;
        }

        if (this.sashes.length === 3) {
            this.sashes[1].handlePosition = handlePosition;
            return;
        }
    }

    private mirrorSashes(selectedHingeSideId)
    {
        if (selectedHingeSideId === this.DEFAULT_HINGE_SIDE_ID) {
            this.sashes = this.initialSashes;
            return;
        }

        if (selectedHingeSideId === 2) {
            this.sashes = this.initialSashes.reverse();
            this.sashes.forEach(sash => {
                sash.handlePosition = sash.handlePosition === 'R' ? 'L' : 'R';
            });
        }
    }

    /**
     * We could move this to the backend part and prepare indexes in the ProductDataComponent (getProductLayout())
     * I've made this like this because it's easier this way, because otherwise we would have to make some hacks in the
     * ProductValidationComponent to make getProductLayout () works.
     *
     * For now we just need to set these indexes for pair doors and 4-sash sliding doors to set information which sash
     * is active (number "1") and not active (number "2").
     *
     * For pair doors active sash means sash with the handle. not active sash is without the handle because on pair doors
     * there is a floating mullion.
     *
     * For sliding doors active sash means the sash which we have to open first to be able to open other one (because of
     * some cylinder in the mechanism.
     */
    private initializeSashIndexes()
    {
        const ACTIVE_SASH_INDEX = 1;
        const NOT_ACTIVE_SASH_INDEX = 2;
        const isDefaultInsideView = this.productDataStore.getAttributeByName('defaultInsideView');

        if (
            this.productDataStore.getAttributeByName('isWindowDoorOrPairDoors') &&
            this.standardFields === 2
        ) {
            let avtiveSashIndexWasSet = false;
            this.sashes.forEach(sash => {
                if (sash.sashType === 'DK') {
                    sash.index = ACTIVE_SASH_INDEX;
                    avtiveSashIndexWasSet = true;
                } else {
                    sash.index = NOT_ACTIVE_SASH_INDEX;
                }
            });
            if (!avtiveSashIndexWasSet) {
                this.sashes.some(sash => {
                    if (sash.sashType !== 'F' && sash.sashType !== 'FF') {
                        sash.index = ACTIVE_SASH_INDEX;
                        avtiveSashIndexWasSet = true;
                    }

                    return avtiveSashIndexWasSet;
                });
            }
        }

        if (
            this.productDataStore.getAttributeByName('isSlidingDoors') &&
            this.standardFields === 4
        ) {

            this.sashes.forEach((sash, index) => {
                sash.index = null;

                if (isDefaultInsideView && index === 1) {
                    sash.index = ACTIVE_SASH_INDEX;
                }

                if (isDefaultInsideView && index === 2) {
                    sash.index = NOT_ACTIVE_SASH_INDEX;
                }

                if (!isDefaultInsideView && index === 1) {
                    sash.index = NOT_ACTIVE_SASH_INDEX;
                }

                if (!isDefaultInsideView && index === 2) {
                    sash.index = ACTIVE_SASH_INDEX;
                }
            });
        }
    }

    private hasFixedInSashAccessory()
    {
        this.hasFixedInSash = false;
        const FIXED_IN_SASH_ACCESSORY_TYPE = 2;
        const selectedAccessories = this.productDataStore.getSelectedOptionByName('accessories');
        selectedAccessories.forEach(accessory => {
            const accessoryData = this.productDataStore.getAvailableOptionByNameAndId('accessory', accessory.id);
            if (accessoryData.attributes.accessoryType === FIXED_IN_SASH_ACCESSORY_TYPE ) {
                this.hasFixedInSash = true;
                return true;
            }
        });
    }
}
